<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
        Pending Invitations
      </h3>
    </div>

    <!-- Empty State -->
    <div v-if="invitations.length === 0" class="px-6 py-12 text-center">
      <svg
        class="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
        />
      </svg>
      <p class="mt-2 text-gray-600 dark:text-gray-400">No pending invitations</p>
    </div>

    <!-- Loading State -->
    <div v-else-if="isLoading" class="px-6 py-12 text-center">
      <div class="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400">
        <svg class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>Loading...</span>
      </div>
    </div>

    <!-- Invitations List -->
    <div v-else class="divide-y divide-gray-200 dark:divide-gray-700">
      <div
        v-for="invitation in invitations"
        :key="invitation.id"
        class="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            <!-- Username -->
            <p class="text-sm font-medium text-gray-900 dark:text-white">
              {{ invitation.username }}
            </p>

            <!-- Contact Info (Phone or Email) -->
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
              <span
                v-if="invitation.email"
                class="inline-flex items-center gap-1"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {{ invitation.email }}
              </span>
              <span v-else class="inline-flex items-center gap-1">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {{ formatPhoneNumber(invitation.phone_number) }}
              </span>
            </p>

            <!-- Permissions -->
            <div class="flex flex-wrap gap-1 mt-2">
              <span
                v-for="permission in invitation.permissions"
                :key="permission"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
              >
                {{ formatPermission(permission) }}
              </span>
            </div>

            <!-- Expiry Info -->
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Expires: {{ formatDate(invitation.expires_at) }}
              <span v-if="isExpired(invitation.expires_at)" class="text-red-600 dark:text-red-400">
                (Expired)
              </span>
              <span v-else-if="expiresIn24Hours(invitation.expires_at)" class="text-amber-600 dark:text-amber-400">
                (Expires soon)
              </span>
            </p>
          </div>

          <!-- Actions -->
          <div class="flex gap-2">
            <button
              @click="copyInviteLink(invitation)"
              :disabled="isRevoking === invitation.id"
              title="Copy invite link"
              class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              @click="revokeInvitation(invitation.id)"
              :disabled="isRevoking === invitation.id"
              title="Revoke invitation"
              class="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition disabled:opacity-50"
            >
              <svg
                v-if="isRevoking === invitation.id"
                class="w-5 h-5 animate-spin"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="px-6 py-4 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
      <p class="text-sm text-red-700 dark:text-red-300">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';

export default {
  name: 'PendingInvitationsList',
  props: {
    storeId: {
      type: [String, Number],
      required: true,
    },
    invitations: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['revoke', 'refresh'],
  setup(props, { emit }) {
    const isRevoking = ref(null);
    const errorMessage = ref('');

    const formatPhoneNumber = (phone) => {
      if (!phone) return '';
      return phone;
    };

    const formatPermission = (permission) => {
      return permission
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    };

    const formatDate = (date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const isExpired = (date) => {
      if (!date) return false;
      return new Date(date) < new Date();
    };

    const expiresIn24Hours = (date) => {
      if (!date) return false;
      const now = new Date();
      const expireTime = new Date(date);
      const hoursUntilExpire = (expireTime - now) / (1000 * 60 * 60);
      return hoursUntilExpire > 0 && hoursUntilExpire < 24;
    };

    const copyInviteLink = (invitation) => {
      // TODO: Generate and copy invite link to clipboard
      const link = `${window.location.origin}/accept-invitation/${invitation.id}`;
      navigator.clipboard.writeText(link);
      // Show toast notification
    };

    const revokeInvitation = async (invitationId) => {
      if (!confirm('Are you sure you want to revoke this invitation?')) {
        return;
      }

      isRevoking.value = invitationId;
      errorMessage.value = '';

      try {
        emit('revoke', invitationId);
      } catch (error) {
        errorMessage.value = error.message || 'Failed to revoke invitation';
      } finally {
        isRevoking.value = null;
      }
    };

    return {
      isRevoking,
      errorMessage,
      formatPhoneNumber,
      formatPermission,
      formatDate,
      isExpired,
      expiresIn24Hours,
      copyInviteLink,
      revokeInvitation,
    };
  },
};
</script>
